import React, {useEffect, useState} from 'react';
import {getSystem, TypeImplementation} from '@packages/systems/wfdl';
import {updateExpressionAction} from '@packages/systems/ExpressionEditor-utils';
import {TNumber, ENumber} from '@packages/systems/wfdl-ast';
import {Ok} from '@packages/utilities/fp/result';
import WebflowUnitInput from '@packages/systems/design-system/WebflowUnitInput';
import {isValidNumber} from '@packages/systems/core/utils/NumberUtils';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
  getAnn,
} from '@packages/utilities/contextual-expression';

type Props = {
  unit: string;
  min: number;
  max: number;
  step: number;
};

const renderNumberUnit =
  ({unit, min, max, step}: Props) =>
  ({
    expr,
    dispatch,
  }: {
    expr: ContextualExpression;
    dispatch: TypeImplementation.ExpressionEditorDispatch;
  }) => {
    const {type} = getAnn(expr);

    if (type === null) {
      throw Error('Expected type from AnnotatedExpression to be defined');
    }

    const system = getSystem(type);

    const defaultValue = system?.defaultValue || 0;

    const [inputValue, setInputValue] = useState<number>(
      ContextualExpressions.getNumber(stripAnn(expr)) || defaultValue
    );

    useEffect(() => {
      const exprValue = ContextualExpressions.getNumber(stripAnn(expr));
      if (exprValue !== null) {
        setInputValue(exprValue);
      }
    }, [expr]);

    // parses string value to number.
    const parseValue = (_unit: string, v: string) => {
      // Removes unit if user included it
      const regExUnit = new RegExp(unit, 'gi');
      if (typeof v === 'string') {
        v = v.replace(regExUnit, '');
      }
      let value = parseFloat(v);
      // Reverts to default if input contained string other than number or  number + unit.
      // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type 'number'.
      if (isNaN(v)) {
        value = defaultValue;
      }
      return Ok({
        value,
        unit,
      });
    };
    const getNumberFromValue = (v: {unit: any; value: any}) => v.value;

    const getUnit = () => unit;
    const getValue = (value: undefined | number | string) => ({
      value,
      unit,
    });

    const unitData = [
      {unit, range: {min, max, step, default: defaultValue}, menuName: unit},
    ];

    const onChange = (v: {unit: any; value: any}) => {
      dispatch(updateExpressionAction(ENumber(v.value)));
    };

    const isValidInputValue = (_: string, input: string) =>
      isValidNumber(input);
    return (
      <WebflowUnitInput
        onChange={onChange}
        isValidInputValue={isValidInputValue}
        getValue={getValue}
        getUnit={getUnit}
        units={unitData}
        value={{unit, value: inputValue}}
        parseValue={parseValue}
        getNumberFromValue={getNumberFromValue}
        placeholder={{unit, value: defaultValue}}
      />
    );
  };

export const MSNumber = TypeImplementation.Alias({
  type: TNumber,
  render: renderNumberUnit({
    unit: 'MS',
    step: 100,
    min: 0,
    max: Number.MAX_SAFE_INTEGER,
  }),
});

export const PXNumber = TypeImplementation.Alias({
  type: TNumber,
  render: renderNumberUnit({
    unit: 'PX',
    step: 1,
    min: 0,
    max: Number.MAX_SAFE_INTEGER,
  }),
});

export const MSNumberCartWrapper = TypeImplementation.Alias({
  type: TNumber,
  render: renderNumberUnit({
    unit: 'MS',
    step: 50,
    min: 0,
    max: 1000,
  }),
});
